<template>
  <section data-testid="cycle-digests-section">
    <div class="fc-notification-section-head">
      <v-icon class="fc-notification-section-head__icon">mdi-view-dashboard-outline</v-icon>
      <div>
        <div class="fc-notification-section-head__title">Dashboard digests</div>
        <div class="fc-notification-section-head__sub">A summary of this cycle's dashboard, by email.</div>
      </div>
      <span class="fc-notification-section-head__spacer" />
      <v-btn
        v-if="hasActiveColumnFilter"
        text
        small
        class="fc-notification-toolbar__clear text-none mr-2"
        data-testid="cycle-digests-section__clear-filter"
        @click="clearColumnFilters"
      >
        Clear Filter
      </v-btn>
      <v-btn
        class="secondaryBtn text-none"
        small
        :disabled="closed"
        data-testid="cycle-digests-section__add"
        @click="$emit('edit', null)"
      >
        <v-icon left small>mdi-plus</v-icon>
        New digest
      </v-btn>
    </div>
    <EmailNotificationsTabSkeleton
      v-if="showSkeleton"
      variant="cycle-table"
      data-testid="cycle-digests-section__skeleton"
    />
    <NotificationsTable
      v-else
      ref="notificationsTable"
      class="notifications-table--digest"
      :items="items"
      :headers="headers"
      :row-enricher="tableFilterRow"
      :loading="loading"
      @filter-active-change="hasActiveColumnFilter = $event"
      :show-toolbar="false"
      empty-text="No digests in this cycle"
      data-testid-prefix="cycle-digests-section"
    >
      <template #cell-name="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ displayName(item) }}</div>
          <div v-if="fromTemplate(item)" class="fc-notification-from-template">
            <NotificationKindBadge kind="template" />
          </div>
        </div>
      </template>
      <template #cell-subject="{ item }">
        <div class="fc-notification-name">
          <div class="fc-notification-name__title">{{ item.subject || '—' }}</div>
        </div>
      </template>
      <template #cell-recipients="{ item }">
        <div class="fc-notification-inline">
          <NotificationRecipientChips :items="recipientList(item)" />
        </div>
      </template>
      <template #cell-sendsAt="{ item }">
        <NotificationScheduleCell
          :schedule="item.meta && item.meta.schedule"
          :cron="item.schedulerTiming"
          kind="schedule"
        />
      </template>
      <template #cell-activeWindow="{ item }">{{ activeWindow(item) }}</template>
      <template #cell-status="{ item }">
        <NotificationStatusToggle
          :value="item.flag === true"
          :disabled="closed"
          data-testid="cycle-digests-section__status-toggle"
          @change="onToggle(item, $event)"
        />
      </template>
      <template #actions="{ item }">
        <NotificationActionIcon
          :tooltip="EMAIL_NOTIFICATION.ACTION_TOOLTIP.EDIT"
          :icon-src="EMAIL_NOTIFICATION.ACTION_ICON.EDIT"
          :disabled="closed"
          data-testid="cycle-digests-section__edit-btn"
          @click="$emit('edit', item)"
        />
        <ActivityLog :resource-id="item._id" module-name="financialclose_notification" />
        <NotificationDeleteAction
          :notification="item"
          :disabled="closed"
          confirm-body="Remove this notification from the cycle?"
          data-testid="cycle-digests-section__delete-btn"
          @confirm="onDelete(item)"
        />
      </template>
    </NotificationsTable>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { cycleDigestHeaders, EMAIL_NOTIFICATION } from '../../../../../utils/constants';
import {
  cycleEndDateLabel,
  notificationTableFilterRow,
  storedNotificationName,
  recipientListFromMeta,
} from '../../../../../utils/notificationNameUtils';
import ActivityLog from '../../../../../components/ActivityLog/ActivityLog.vue';
import {
  NotificationsTable,
  EmailNotificationsTabSkeleton,
  NotificationStatusToggle,
  NotificationDeleteAction,
  NotificationActionIcon,
  NotificationScheduleCell,
  NotificationRecipientChips,
  NotificationKindBadge,
} from '../../../../../components/Notifications';

export default {
  name: 'CycleDigestsSection',
  components: {
    ActivityLog,
    NotificationsTable,
    EmailNotificationsTabSkeleton,
    NotificationStatusToggle,
    NotificationDeleteAction,
    NotificationActionIcon,
    NotificationScheduleCell,
    NotificationRecipientChips,
    NotificationKindBadge,
  },
  props: {
    cycleId: { type: String, default: '' },
    closed: { type: Boolean, default: false },
  },
  data() {
    return {
      EMAIL_NOTIFICATION,
      hasActiveColumnFilter: false,
      initialFetchPending: true,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', ['fetchLocations']),
    ...mapState('dashboard', ['emailNotifications', 'emailNotificationsLoading']),
    items() {
      return this.emailNotifications || [];
    },
    loading() {
      return this.emailNotificationsLoading;
    },
    headers() {
      return cycleDigestHeaders();
    },
    showSkeleton() {
      if ((this.items || []).length) return false;
      return this.initialFetchPending || this.loading;
    },
  },
  created() {
    this.getAllEmailNotifications({ cycleId: this.cycleId }).finally(() => {
      this.initialFetchPending = false;
    });
  },
  methods: {
    ...mapActions('dashboard', [
      'getAllEmailNotifications',
      'toggleEmailNotificationStatus',
      'deleteEmailNotification',
    ]),
    recipientList(item) {
      return recipientListFromMeta(item.meta);
    },
    tableFilterRow(item) {
      const row = notificationTableFilterRow(item, { locations: this.fetchLocations });
      return { ...row, activeWindow: cycleEndDateLabel(item) };
    },
    displayName(item) {
      return storedNotificationName(item) || '—';
    },
    clearColumnFilters() {
      this.$refs.notificationsTable?.clearFilters();
    },
    fromTemplate(item) {
      return item?.fromTemplate === true || item?.meta?.fromTemplate === true || !!item?.templateId;
    },
    activeWindow(item) {
      return cycleEndDateLabel(item);
    },
    async onToggle(item, next) {
      await this.toggleEmailNotificationStatus({ notification: item, enable: next });
      await this.getAllEmailNotifications({ cycleId: this.cycleId });
    },
    async onDelete(item) {
      await this.deleteEmailNotification({ cycleId: this.cycleId, id: item._id });
    },
  },
};
</script>
